import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import { PRODUCT_BATCHES_QUERY } from './inventory.gql';

import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/shared/ui/card';

interface InventoryBatch {
  id: string;
  product_id: string;
  product_code: string;
  product_name: string;
  lot_number: string;
  expiration_date: string;
  quantity_remaining: string;
  quantity_received: string;
  supplier_name: string | null;
  status: string;
  received_at: string;
}

interface ProductBatchesResponse {
  productBatches: InventoryBatch[];
}

interface ProductBatchesDialogProps {
  product_id: string;
  product_name?: string;
  open: boolean;
  on_close: () => void;
}

export function ProductBatchesDialog({
  product_id,
  product_name,
  open,
  on_close,
}: ProductBatchesDialogProps): JSX.Element | null {
  const { t } = useTranslation();
  const { data, loading } = useQuery<ProductBatchesResponse>(PRODUCT_BATCHES_QUERY, {
    variables: { product_id },
    skip: !open,
  });

  if (!open) return null;

  const batches = data?.productBatches ?? [];

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur"
    >
      <Card className="w-full max-w-3xl">
        <CardHeader>
          <CardTitle>
            {t('inventory.batches_title')}
            {product_name ? <span className="text-muted-foreground"> — {product_name}</span> : null}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="py-6 text-center text-sm text-muted-foreground">{t('common.loading')}</p>
          ) : batches.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">{t('inventory.no_batches')}</p>
          ) : (
            <div className="overflow-auto rounded-md border border-border">
              <table className="w-full text-sm">
                <thead className="bg-secondary/40 text-xs text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 text-left">{t('inventory.lot_number')}</th>
                    <th className="px-3 py-2 text-left">{t('inventory.expiration_date')}</th>
                    <th className="px-3 py-2 text-right">{t('inventory.batch_remaining')}</th>
                    <th className="px-3 py-2 text-right">{t('inventory.batch_received')}</th>
                    <th className="px-3 py-2 text-left">{t('inventory.supplier_name')}</th>
                    <th className="px-3 py-2 text-center">{t('common.status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {batches.map((b) => (
                    <tr key={b.id} className="border-t border-border">
                      <td className="px-3 py-2 font-mono text-xs">{b.lot_number}</td>
                      <td className="px-3 py-2">{b.expiration_date}</td>
                      <td className="px-3 py-2 text-right">{b.quantity_remaining}</td>
                      <td className="px-3 py-2 text-right">{b.quantity_received}</td>
                      <td className="px-3 py-2">{b.supplier_name ?? '—'}</td>
                      <td className="px-3 py-2 text-center">
                        <Badge variant={b.status === 'active' ? 'success' : 'muted'}>
                          {t(`status.${b.status}`, b.status)}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
        <CardFooter className="justify-end">
          <Button type="button" variant="ghost" onClick={on_close}>
            {t('common.close')}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
